// src/customer-form/TicketDetailsFields.jsx
// Cascade: caseType → caseReasonsByType → subReasonsByReason
// All options fetched live from the Zoho portal API via useTicketFields().

import { useTicketFields } from '../hooks/useTicketFields';
import { translations } from './constants';

function getLanguage(country) {
  if (country === 'Morocco') return 'ar';
  if (country === 'Senegal' || country === "Cote D'ivoire") return 'fr';
  return 'en';
}

const SpinnerIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }}
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

export default function TicketDetailsFields({ formData, handleChange, handleBlur, onCaseTypeChange, onCaseReasonChange, errors = {}, touched = {} }) {
  const lang = getLanguage(formData.country);
  const t = translations[lang];

  const { fields, isLoading, error } = useTicketFields();

  // Derive cascaded option lists from the live API data
  const caseTypeOptions    = fields?.caseTypeOptions    ?? [];
  const caseReasonsByType  = fields?.caseReasonsByType  ?? {};
  const subReasonsByReason = fields?.subReasonsByReason ?? {};

  const availableReasons    = formData.caseType   ? (caseReasonsByType[formData.caseType]     ?? []) : [];
  const availableSubReasons = formData.caseReason ? (subReasonsByReason[formData.caseReason]  ?? []) : [];
  const hasSubReasons       = availableSubReasons.length > 0;

  // Escalation, issueType, documentType still use translations (multilingual)
  // but their allowed values come from the API when loaded
  const escalationOptions  = fields?.escalationOptions   ?? t.escalationPathOptions;
  const issueTypeOptions   = fields?.issueTypeOptions    ?? t.issueTypeOptions;
  const documentTypeOptions = fields?.documentTypeOptions ?? t.documentTypeOptions;

  // Cascade resets are handled atomically in the parent via dedicated props
  const handleCaseTypeChange = (e) => onCaseTypeChange(e.target.value);
  const handleCaseReasonChange = (e) => onCaseReasonChange(e.target.value);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '24px 0', color: 'var(--text-muted)', fontSize: 14 }}>
        <SpinnerIcon />
        Loading form options…
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '16px', background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: 10, color: '#c53030', fontSize: 13 }}>
        ⚠ Could not load ticket options: {error}. Please refresh the page.
      </div>
    );
  }

  return (
    <div className="row2">
      {/* 1. Case Type */}
      <div className={`field ${touched.caseType && errors.caseType ? 'has-error' : ''}`}>
        <label className="label">{t.caseType} <span className="required">*</span></label>
        {touched.caseType && errors.caseType && <div className="error-message">{errors.caseType}</div>}
        <select
          className="select"
          name="caseType"
          value={formData.caseType}
          onChange={handleCaseTypeChange}
          onBlur={handleBlur}
          required
        >
          <option value="">{t.selectCaseType}</option>
          {caseTypeOptions.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
      </div>

      {/* 2. Case Type Reason — filtered by caseType */}
      <div className={`field ${touched.caseReason && errors.caseReason ? 'has-error' : ''}`}>
        <label className="label">{t.caseReason} <span className="required">*</span></label>
        {touched.caseReason && errors.caseReason && <div className="error-message">{errors.caseReason}</div>}
        <select
          className="select"
          name="caseReason"
          value={formData.caseReason}
          onChange={handleCaseReasonChange}
          onBlur={handleBlur}
          required
          disabled={!formData.caseType}
        >
          <option value="">
            {formData.caseType ? t.selectCaseReason : '— select Case Type first —'}
          </option>
          {availableReasons.map((reason) => (
            <option key={reason} value={reason}>{reason}</option>
          ))}
        </select>
      </div>

      {/* 3. Case Type Sub-reason — only shown when the selected reason has sub-reasons */}
      {hasSubReasons && (
        <div className={`field ${touched.subReason && errors.subReason ? 'has-error' : ''}`}>
          <label className="label">{t.subReason} <span className="required">*</span></label>
          {touched.subReason && errors.subReason && <div className="error-message">{errors.subReason}</div>}
          <select
            className="select"
            name="subReason"
            value={formData.subReason}
            onChange={handleChange}
            onBlur={handleBlur}
            required
          >
            <option value="">{t.selectSubReason}</option>
            {availableSubReasons.map((sub) => (
              <option key={sub} value={sub}>{sub}</option>
            ))}
          </select>
        </div>
      )}

      {/* 4. Type of Issue */}
      <div className={`field ${touched.issueType && errors.issueType ? 'has-error' : ''}`}>
        <label className="label">{t.issueType}</label>
        {touched.issueType && errors.issueType && <div className="error-message">{errors.issueType}</div>}
        <select
          className="select"
          name="issueType"
          value={formData.issueType}
          onChange={handleChange}
          onBlur={handleBlur}
        >
          <option value="">{t.selectIssueType}</option>
          {issueTypeOptions.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
      </div>

      {/* 5. Escalation Path */}
      <div className={`field ${touched.escalationPath && errors.escalationPath ? 'has-error' : ''}`}>
        <label className="label">{t.escalationPath} <span className="required">*</span></label>
        {touched.escalationPath && errors.escalationPath && <div className="error-message">{errors.escalationPath}</div>}
        <select
          className="select"
          name="escalationPath"
          value={formData.escalationPath}
          onChange={handleChange}
          onBlur={handleBlur}
          required
        >
          <option value="">{t.selectEscalationPath}</option>
          {escalationOptions.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
      </div>

      {/* 6. Document Type */}
      <div className={`field ${touched.documentType && errors.documentType ? 'has-error' : ''}`}>
        <label className="label">{t.documentType}</label>
        {touched.documentType && errors.documentType && <div className="error-message">{errors.documentType}</div>}
        <select
          className="select"
          name="documentType"
          value={formData.documentType}
          onChange={handleChange}
          onBlur={handleBlur}
        >
          <option value="">{t.selectDocumentType}</option>
          {documentTypeOptions.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
